import { resolvePowerProfileReference } from './powerProfileRanges';
import { getZoneDetails, zoneColor, zoneLabelOrder } from './zoneSeries';
import { DONUT_PALETTE, SPORT_COLORS } from './palette';
import type { PmcPt, DonutSeg, WeekBar, WeekZones, HrZone, PowerProfileGroup } from './types';

export function n(v: unknown): number {
  return parseFloat(String(v ?? 0)) || 0;
}

export function unwrapData(raw: unknown): unknown[] {
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === 'object') {
    const r = raw as Record<string, unknown>;
    if (Array.isArray(r.data)) return r.data;
    if (r.data && typeof r.data === 'object') {
      const inner = r.data as Record<string, unknown>;
      if (Array.isArray(inner.data)) return inner.data;
    }
  }
  return [];
}

export function normPmc(raw: unknown): PmcPt[] {
  return unwrapData(raw).map((d: any) => ({
    date: d.date ?? d.calculation_date ?? new Date().toISOString().slice(0, 10),
    ctl: n(d.ctl), atl: n(d.atl), tsb: n(d.tsb),
    tss: d.tss != null ? n(d.tss) : null,
  }));
}

/** Normalises the bundled PMC payload from /all-charts.
 *  The API pre-packages date windows in last_7_days / last_28_days /
 *  last_90_days / last_180_days / last_365_days / last_730_days;
 *  each window's `default` is a date→{ctl,atl,tsb} dict.
 *  When a custom range is requested (days === -1 or very large), all windows
 *  contain the same range data — we use last_7_days as fallback. */
export function normPmcBundled(data: unknown, days: number): PmcPt[] {
  const key =
    days <= 14  ? 'last_7_days'  :
    days <= 30  ? 'last_28_days' :
    days <= 90  ? 'last_90_days' :
    days <= 180 ? 'last_180_days' :
    days <= 365 ? 'last_365_days' : 'last_730_days';
  const rd = (data as any)?.[key];
  if (rd?.default && typeof rd.default === 'object' && !Array.isArray(rd.default)) {
    return Object.entries(rd.default as Record<string, any>)
      .map(([date, v]: [string, any]) => ({
        date, ctl: n(v.ctl), atl: n(v.atl), tsb: n(v.tsb),
        tss: v.tss != null ? n(v.tss) : null,
      }))
      .sort((a, b) => a.date.localeCompare(b.date));
  }
  return normPmc(data);
}

/** Formats a donut segment value — seconds → H:MM for duration metrics, otherwise integer. */
function formatDonutValue(rawVal: number, fitnessType?: string): string {
  const v = Math.round(rawVal);
  if (fitnessType === 'total_time' || fitnessType === 'planned_duration') {
    const hh = Math.floor(v / 3600);
    const mm = Math.round((v % 3600) / 60);
    return `${hh}:${String(mm).padStart(2, '0')}`;
  }
  return String(v);
}

export function normDonut(raw: unknown, fitnessType?: string): DonutSeg[] {
  const arr = unwrapData(raw) as any[];
  if (!arr.length) return [];
  // fitness_summery_chart returns {fitness_value, title}; sport-breakdown returns pct/hours
  const getVal = (d: any) =>
    Math.round(n(d.pct ?? d.percentage ?? d.total_time ?? d.hours ?? d.fitness_value ?? 0));
  const total = arr.reduce((s, d) => s + getVal(d), 0);
  if (!total) return [];
  return arr.map((d: any, i: number) => {
    const rawVal = getVal(d);
    const label = d.label ?? d.sport_type ?? d.name ?? d.title ?? `Type ${i + 1}`;
    const rawHrs = n(d.hours ?? 0);
    const hrs = rawHrs > 0 && d.fitness_value == null && d.hours != null
      ? (() => {
          const hh = Math.floor(rawHrs), mm = Math.round((rawHrs - hh) * 60);
          return `${hh}:${String(mm).padStart(2, '0')}`;
        })()
      : formatDonutValue(rawVal, fitnessType);
    return {
      label,
      pct: Math.round((rawVal / total) * 100),
      color: SPORT_COLORS[label.toLowerCase()] ?? DONUT_PALETTE[i % DONUT_PALETTE.length],
      hrs,
      rawVal,
    };
  }).filter((d) => d.pct > 0);
}

export function normBars(raw: unknown, valueKey?: string): WeekBar[] {
  return unwrapData(raw).map((d: any, i: number) => {
    const val = valueKey
      ? n(d[valueKey])
      // nutrition_chart uses calories_expended; fitness_summery_report uses duration
      : n(d.value ?? d.total ?? d.duration ?? d.distance ?? d.calories ??
           d.calories_expended ?? d.calories_consumed ?? 0);
    // week_start from nutrition weekly; workout_day from daily; title from per-sport-type charts
    const rawLbl = String(d.week_start ?? d.week ?? d.label ?? d.workout_day ?? d.date ?? d.title ?? (i + 1));

    return { label: rawLbl, value: val };
  }).filter((b) => b.value > 0);
}

/** Normalises the complex periodic_chart payload { workout_summery?, pmc?, metrics?, ... }
 *  Falls back to normBars when the payload is a flat array. */
export function normPeriodicChart(raw: unknown, metricKey?: string): WeekBar[] {
  const displayValue = (value: number) => metricKey === 'total_time' ? value / 60 : metricKey === 'distance' ? value / 1000 : value;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return normBars(raw).map(bar => ({ ...bar, value: displayValue(bar.value) }));
  const r = raw as Record<string, unknown>;

  const ws = r.workout_summery ?? r.workout_summary;
  if (Array.isArray(ws) && ws.length) {
    const baseKeys = ['tss_actual', 'total_time', 'distance', 'calories', 'work', 'if_actual'];
    const preferredKeys = metricKey
      ? [metricKey, ...baseKeys.filter((k) => k !== metricKey)]
      : baseKeys;

    return (ws as any[]).map((d: any, i: number) => {
      // API returns SUM() columns as strings — do not rely on typeof === 'number'
      let val = 0;
      for (const key of preferredKeys) {
        if (d[key] != null && d[key] !== '') {
          val = n(d[key]);
          break;
        }
      }
      if (!val) {
        const fallbackKey = Object.keys(d).find(
          (k) => k !== 'workout_day' && d[k] != null && d[k] !== '' && !Number.isNaN(n(d[k])),
        );
        val = fallbackKey ? n(d[fallbackKey]) : 0;
      }
      val = displayValue(val);
      const rawLbl = String(d.workout_day ?? i + 1);

      return { label: rawLbl, value: val };
    }).filter(b => b.value > 0);
  }

  // Fallback: PMC tss_actual series
  const pmc = r.pmc;
  if (Array.isArray(pmc) && pmc.length) {
    return (pmc as any[]).map((d: any, i: number) => {
      const val = n(d.tss_actual ?? d.ctl ?? 0);
      const rawLbl = String(d.calculation_date ?? i + 1);

      return { label: rawLbl, value: val };
    }).filter(b => b.value > 0);
  }
  return [];
}

/** Same order + gradients as Ai-Tempo PowerProfile.tsx */
const PP_ORDERED_KEYS = ['power5sec', 'power1min', 'power5min', 'power20min'] as const;
const PP_COLORS: Record<string, string> = {
  power5sec: '#58E0D4', power1min: '#F8C968', power5min: '#B48BFF', power20min: '#EA4782',
};
const PP_COLORS2: Record<string, string> = {
  power5sec: '#13A89E', power1min: '#E0A020', power5min: '#6C3CEB', power20min: '#B5185A',
};
const PP_LABELS: Record<string, string> = {
  power5sec: '5s', power1min: '1min', power5min: '5min', power20min: '20min',
};

function parseWorkoutId(value: unknown): number | undefined {
  if (value == null || value === '') return undefined;
  const id = typeof value === 'number' ? value : parseInt(String(value), 10);
  return Number.isFinite(id) && id > 0 ? id : undefined;
}

/** Normalises the power_profile payload { report: { power5sec, … }, Yaxis }
 *  Returns grouped vertical bars per interval — mirrors Ai-Tempo PowerProfile data shape. */
export function normPowerProfile(raw: unknown): PowerProfileGroup[] {
  const report = (raw as any)?.report ?? raw;
  if (!report || typeof report !== 'object') return [];
  return PP_ORDERED_KEYS.flatMap((key) => {
    const d = (report as any)[key];
    if (!d) return [];
    const items: any[] = Array.isArray(d) ? d : [d];
    const c1 = PP_COLORS[key] ?? '#8E7BF0';
    const c2 = PP_COLORS2[key] ?? c1;
    const bars = items
      .filter((item) => item && (n(item.normalized_value) > 0 || n(item.watts_per_kg) > 0))
      .map((item) => ({
        wkg: n(item.watts_per_kg),
        normalized: Math.min(100, Math.max(0, n(item.normalized_value))),
        category: String(item.category ?? 'Untrained'),
        workoutId: parseWorkoutId(item.workout_id),
        startDate: item.start_date ? String(item.start_date).slice(0, 10) : undefined,
        endDate: item.end_date ? String(item.end_date).slice(0, 10) : undefined,
        power: item.power != null ? n(item.power) : undefined,
        weight: item.weight != null ? n(item.weight) : undefined,
        color: c1,
        color2: c2,
      }));
    if (!bars.length) return [];
    return [{
      referenceSex: items.map(item => resolvePowerProfileReference(key, String(item?.category), item?.min_range, item?.max_range)).find(Boolean),
      intervalKey: key,
      interval: PP_LABELS[key] ?? key,
      color: c1,
      color2: c2,
      bars,
    }];
  });
}

/** Keep the athlete's recorded zone labels, including zero-time and >5 zones. */
export function normZonesByWeek(raw: unknown): WeekZones[] {
  const weeks = unwrapData(raw).map((d: any, i: number) => {
    const weekStr = String(d.startDate ?? d.start_date ?? d.week ?? d.date ?? i + 1);
    const entries = d.zones && typeof d.zones === 'object' && !Array.isArray(d.zones)
      ? Object.entries(d.zones).map(([label, minutes]) => ({ label, hrs: n(minutes) / 60 }))
      : Object.entries(d).flatMap(([key, value]) => {
          const match = key.match(/^(?:z|zone_)(\d+)$/);
          return match ? [{ label: `Z${match[1]}`, hrs: n(value) }] : [];
        });
    return {
      week: weekStr,
      startDate: String(d.startDate ?? d.start_date ?? d.date ?? '').slice(0, 10) || undefined,
      endDate: String(d.endDate ?? d.end_date ?? '').slice(0, 10) || undefined,
      entries: entries.map(zone => ({ ...zone, hrs: Number.isFinite(zone.hrs) ? Math.max(0, zone.hrs) : 0 })),
    };
  });
  const labels = [...new Set(weeks.flatMap(week => week.entries.map(zone => zone.label)))]
    .sort((a, b) => zoneLabelOrder(a) - zoneLabelOrder(b));
  return weeks.map(({ entries, ...week }) => {
    const values = new Map(entries.map(zone => [zone.label, zone.hrs]));
    const zoneDetails = labels.map((label, i) => ({ label, hrs: values.get(label) ?? 0, color: zoneColor(label, i) }));
    // Legacy fields are kept for callers; rendering and totals use all zoneDetails.
    const numbered = labels.every(label => /^Z\d+$/.test(label));
    const legacy = Object.fromEntries([1, 2, 3, 4, 5].map(i => [`z${i}`, numbered ? values.get(`Z${i}`) ?? 0 : zoneDetails[i - 1]?.hrs ?? 0])) as
      Pick<WeekZones, 'z1' | 'z2' | 'z3' | 'z4' | 'z5'>;
    return { ...week, ...legacy, zoneDetails };
  }).filter(week => week.zoneDetails.some(zone => zone.hrs > 0));
}

export function deriveHrZones(zonesByWeek: WeekZones[]): HrZone[] {
  const totals = new Map<string, HrZone>();
  zonesByWeek.forEach(week => getZoneDetails(week).forEach(zone => {
    const existing = totals.get(zone.label);
    totals.set(zone.label, { lbl: zone.label, color: zone.color, hrs: (existing?.hrs ?? 0) + zone.hrs });
  }));
  return [...totals.values()].filter(zone => zone.hrs > 0);
}

/** Synthetic PMC for when the API returns no data (demo / offline). */
export function generatePmc(nDays: number): PmcPt[] {
  let ctl = 44, atl = 44;
  const out: PmcPt[] = [];
  for (let i = 0; i < nDays; i++) {
    const wk = Math.floor(i / 7), dow = i % 7;
    const isRec = wk % 4 === 3, isTaper = i >= nDays - 14;
    let tss = 0;
    if (dow !== 0) {
      if (isTaper)     tss = dow === 3 ? 75 : 38 + Math.random() * 28;
      else if (isRec)  tss = 38 + Math.random() * 38;
      else             tss = [2, 4, 6].includes(dow) ? 88 + Math.random() * 82 : 48 + Math.random() * 40;
      tss = Math.round(tss);
    }
    const pCtl = ctl, pAtl = atl;
    ctl = ctl + (tss - ctl) / 42;
    atl = atl + (tss - atl) / 7;
    const d = new Date(); d.setDate(d.getDate() - (nDays - 1 - i));
    out.push({ date: d.toISOString().slice(0, 10), tss: tss || null, ctl, atl, tsb: pCtl - pAtl });
  }
  return out;
}

/** Normalises metrics_chart payload { series: [{ name, points: [{date, value}] }] }. */
export function normMetricsChart(raw: unknown): import('./types').MetricTrendSeries[] {
  if (!raw || typeof raw !== 'object') return [];
  const payload = raw as { series?: unknown };
  if (!Array.isArray(payload.series)) return [];

  return payload.series
    .map((s: any) => ({
      metric_type_id: Number(s?.metric_type_id ?? 0),
      name: String(s?.name ?? 'Metric'),
      unit: s?.unit != null ? String(s.unit) : s?.metric_unit != null ? String(s.metric_unit) : undefined,
      data_type: s?.data_type != null ? String(s.data_type) : undefined,
      points: Array.isArray(s?.points)
        ? s.points
            .map((p: any) => ({
              date: String(p?.date ?? ''),
              value: typeof p?.value === 'number' ? p.value : typeof p?.value === 'string' && p.value.trim() !== '' ? Number(p.value) : Number.NaN,
            }))
            .filter((p: { date: string; value: number }) => /^\d{4}-\d{2}-\d{2}$/.test(p.date) && Number.isFinite(Date.parse(p.date)) && Number.isFinite(p.value))
            .sort((a: { date: string }, b: { date: string }) => a.date.localeCompare(b.date))
        : [],
    }))
    .filter((s) => s.points.length > 0);
}
